/**
 * Minimal Zod resolver for react-hook-form.
 *
 * `react-hook-form` and `zod` are both already dependencies of this app, but
 * `@hookform/resolvers` is not. This is the small part of that package we
 * actually need, so Access forms can use RHF + Zod without adding a dependency.
 *
 * Behaviour matches @hookform/resolvers/zod for the cases we use: on success the
 * PARSED values are returned (so Zod defaults and coercions take effect); on
 * failure, issues are mapped to RHF's `errors` shape keyed by dotted path.
 *
 * The schema is typed structurally rather than as `ZodType` so this file does
 * not have to track Zod's generic signature across major versions.
 */

import type { FieldValues, Resolver } from 'react-hook-form';

interface ZodIssueLike {
  path: PropertyKey[];
  message: string;
  code?: string;
}

type ZodResultLike =
  | { success: true; data: unknown }
  | { success: false; error: { issues: ZodIssueLike[] } };

export interface ZodSchemaLike {
  safeParse(value: unknown): ZodResultLike;
}

export function zodResolver<TFieldValues extends FieldValues>(
  schema: ZodSchemaLike
): Resolver<TFieldValues> {
  return async (values) => {
    const result = schema.safeParse(values);

    if (result.success) {
      return { values: result.data as TFieldValues, errors: {} };
    }

    const errors: Record<string, { type: string; message: string }> = {};

    for (const issue of result.error.issues) {
      // Only the FIRST issue per field is kept — RHF renders one message per
      // field, and later issues on a path are usually consequences of the first.
      const path = issue.path.map(String).join('.') || 'root';
      if (!errors[path]) {
        errors[path] = { type: issue.code ?? 'validation', message: issue.message };
      }
    }

    return { values: {} as TFieldValues, errors: errors as never };
  };
}
